<template>
  <div class="car-inout-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('carInoutManage.page.title')" :subtitle="$t('carInoutManage.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：停车场区域选择 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-panel ds-role-tree">
          <div class="ds-role-tree__title">{{ $t('carInoutManage.page.parkingAreaTitle') }}</div>
          <ul class="ds-role-tree__list">
            <li v-for="(item, index) in parkingAreas" :key="index" class="ds-role-tree__item"
              :class="{ 'is-active': conditions.paNum === item.num }" @click="swatchParkingArea(item)">
              {{ item.num }}
            </li>
          </ul>
          <div v-if="!parkingAreas.length" class="ds-role-tree__empty">
            {{ $t('carInoutManage.page.emptyArea') }}
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.carNum" clearable class="w-full"
                :placeholder="$t('carInoutManage.carNumRequired')" @keyup.enter.native="queryCarInoutMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.state" clearable class="w-full"
                :placeholder="$t('carInoutManage.selectCarStatus')">
                <el-option :label="$t('carInoutManage.selectCarStatus')" value="" />
                <el-option :label="$t('carInoutManage.inStatus')" value="100300" />
                <el-option :label="$t('carInoutManage.payCompleted')" value="100400" />
                <el-option :label="$t('carInoutManage.outStatus')" value="100500" />
                <el-option :label="$t('carInoutManage.payTimeout')" value="100600" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.carType" clearable class="w-full"
                :placeholder="$t('carInoutManage.selectCarType')">
                <el-option :label="$t('carInoutManage.selectCarType')" value="" />
                <el-option :label="$t('carInoutManage.temporaryCar')" value="1003" />
                <el-option :label="$t('carInoutManage.saleCar')" value="S" />
                <el-option :label="$t('carInoutManage.monthlyCar')" value="1001" />
                <el-option :label="$t('carInoutManage.internalCar')" value="I" />
                <el-option :label="$t('carInoutManage.freeCar')" value="NM" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('carInoutManage.startTimePlaceholder')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('carInoutManage.endTimePlaceholder')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryCarInoutMethod">
              {{ $t('carInoutManage.query') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetCarInoutMethod">
              {{ $t('carInoutManage.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="listCarInouts">
            <template #left>
              <span class="ds-panel__title">{{ $t('carInoutManage.carInoutRecord') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="carInouts" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('carInoutManage.page.empty')" @retry="listCarInouts">
            <el-table-column :label="$t('carInoutManage.inPhoto')" align="center" width="100">
              <template slot-scope="scope">
                <el-image style="width:60px;height:60px;cursor:pointer;" :src="scope.row.photoJpg || '/img/noPhoto.jpg'"
                  :preview-src-list="[scope.row.photoJpg]" fit="cover" />
              </template>
            </el-table-column>
            <el-table-column prop="inoutId" :label="$t('carInoutManage.inoutId')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inoutId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInoutManage.carStatus')" align="center" width="140">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="scope.row.carInout === '3306' ? 'primary' : 'muted'"
                  :label="scope.row.carInout === '3306' ? $t('carInoutManage.inStatus') : $t('carInoutManage.outStatus')" />
                <div v-if="scope.row.stateName" class="ds-cell-sub">{{ scope.row.stateName }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="carNum" :label="$t('carInoutManage.carNum')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="paNum" :label="$t('carInoutManage.parkingArea')" align="center" width="120" />
            <el-table-column prop="feeName" :label="$t('carInoutManage.feeRule')" align="center" min-width="130"
              show-overflow-tooltip />
            <el-table-column prop="carTypeName" :label="$t('carInoutManage.carType')" align="center" width="110" />
            <el-table-column prop="inTime" :label="$t('carInoutManage.inTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.inTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInoutManage.outTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.carInout === '3307' ? (scope.row.outTime || '-') : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInoutManage.parkingTime')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.hours }}{{ $t('carInoutManage.hour') }}{{ scope.row.min }}{{ $t('carInoutManage.minute') }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('carInoutManage.chargeAmount')" align="right" width="140">
              <template slot-scope="scope">
                <span v-if="scope.row.carType !== 'T'">-</span>
                <span v-else>
                  <span class="ds-num">{{ scope.row.payCharge }}</span>
                  <span v-if="['100300', '100400', '100600'].includes(scope.row.inState)">
                    (<el-link type="primary" @click="carInoutTempCarPayFee(scope.row)">
                      {{ $t('carInoutManage.out') }}
                    </el-link>)
                  </span>
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('carInoutManage.remark')" align="center" min-width="120"
              show-overflow-tooltip />
          </ds-data-table>

          <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listParkingAreas, listCarInouts } from '@/api/car/carInoutManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'CarInoutManageList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      parkingAreas: [],
      carInouts: [],
      conditions: {
        state: '',
        carNum: '',
        carType: '',
        startTime: '',
        endTime: '',
        paNum: '',
        iotApiCode: 'listCarInoutDetailBmoImpl'
      },
      page: {
        current: 1,
        size: 10,
        total: 0,
        records: 0
      }
    }
  },
  created() {
    this.loadParkingArea()
  },
  methods: {
    async loadParkingArea() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: getCommunityId()
        }
        const res = await listParkingAreas(params)
        this.parkingAreas = res.parkingAreas || []
        if (this.parkingAreas.length > 0) {
          this.swatchParkingArea(this.parkingAreas[0])
        }
      } catch (error) {
        console.error('加载停车场区域失败:', error)
      }
    },
    swatchParkingArea(parkingArea) {
      this.conditions.paNum = parkingArea.num
      this.page.current = 1
      this.listCarInouts()
    },
    async listCarInouts() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.conditions,
          page: this.page.current,
          row: this.page.size,
          communityId: getCommunityId()
        }

        // 处理车牌号空格
        if (params.carNum) params.carNum = params.carNum.trim()

        const res = await listCarInouts(params)
        this.carInouts = res.data || []
        this.page.total = res.total || 0
        this.page.records = res.records || 0
      } catch (error) {
        console.error('加载进出场记录失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('carInoutManage.page.fetchError')
        this.$message.error(this.$t('carInoutManage.page.fetchError'))
      } finally {
        this.loading = false
      }
    },
    queryCarInoutMethod() {
      this.page.current = 1
      this.listCarInouts()
    },
    resetCarInoutMethod() {
      this.conditions = {
        state: '',
        carNum: '',
        carType: '',
        startTime: '',
        endTime: '',
        paNum: this.conditions.paNum, // 保留当前停车场
        iotApiCode: 'listCarInoutDetailBmoImpl'
      }
      this.page.current = 1
      this.listCarInouts()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.listCarInouts()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.listCarInouts()
    },
    carInoutTempCarPayFee(row) {
      // 出场支付逻辑
      console.log('出场支付:', row)
    }
  }
}
</script>

<style lang="scss" scoped>
.car-inout-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-role-tree {
  padding: var(--ds-spacing-md) 0;

  &__title {
    padding: 0 var(--ds-spacing-lg) var(--ds-spacing-sm);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-600);
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__item {
    padding: 10px var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    text-align: center;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary-sub);
      border-left-color: var(--ds-primary);
      color: var(--ds-primary);
      font-weight: 600;
    }
  }

  &__empty {
    padding: var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
    text-align: center;
  }
}
</style>
